import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/lib/api-client";
import { Shield, Lock, X, CheckCircle } from "lucide-react";

interface KYCSSNModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const KYCSSNModal = ({ isOpen, onClose, onSuccess }: KYCSSNModalProps) => {
  const [ssn, setSsn] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate SSN (last 4 digits)
    const ssnRegex = /^\d{4}$/;
    if (!ssnRegex.test(ssn)) {
      toast({
        title: "Invalid SSN",
        description: "Please enter the last 4 digits of your Social Security Number.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Register user via KYC with SSN
      const result = await apiClient.post('/customer/withdrawal-methods/register-kyc', {
        ssn: ssn,
      });

      if (result?.success) {
        toast({
          title: "✅ Registration Successful",
          description: "You have been successfully registered for withdrawals. You can now add withdrawal methods.",
          variant: "success",
          duration: 5000,
        });
        setSsn("");
        onSuccess();
        onClose();
      } else {
        throw new Error(result?.error || 'Failed to register');
      }
    } catch (error: any) {
      console.error('Error registering via KYC:', error);
      toast({
        title: "Registration Failed",
        description: error?.message || 'Failed to register. Please try again.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSsnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 4 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setSsn(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-[95vw] max-w-md m-0 p-0 rounded-2xl border border-border bg-background overflow-hidden [&>button]:hidden shadow-2xl shadow-black/50"
        style={{
          background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.95) 50%, rgba(15,23,42,0.98) 100%)',
        }}
      >
        {/* Header */}
        <div className="relative overflow-hidden p-5 border-b border-border">
          {/* Subtle corner accents - blue theme for security */}
          <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-500/10 to-transparent" />
          
          {/* Subtle gradient borders */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

          <div className="relative flex items-center gap-3">
            {/* Icon with glow */}
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 flex-shrink-0"
              style={{
                boxShadow: '0 0 20px hsl(var(--glow-blue) / 0.4)',
              }}
            >
              <Shield className="h-5 w-5 text-white" />
            </div>
            
            <div className="flex-1">
              <h2 className="text-lg font-bold text-white">
                Identity Verification
              </h2>
              <p className="text-xs text-muted-foreground">
                Required for withdrawals
              </p>
            </div>
            
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 rounded-full hover:bg-muted/80 flex-shrink-0 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Security info */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Lock className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80">
              To enable withdrawals, we need to verify your identity. Please enter the last 4 digits of your Social Security Number.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ssn" className="text-sm font-medium text-foreground/80">
                Last 4 Digits of SSN
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                <Input
                  id="ssn"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={ssn}
                  onChange={handleSsnChange}
                  placeholder="••••"
                  className="pl-10 h-12 text-lg font-mono tracking-[0.3em] bg-card border-border text-foreground placeholder:text-muted-foreground/60 focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground/70">
                <CheckCircle className="h-3 w-3 text-green-500" />
                <span>256-bit encrypted • Secure transmission</span>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 h-11 bg-transparent border-border text-foreground/80 hover:bg-muted hover:text-foreground"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || ssn.length !== 4}
                className="flex-1 h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold border-0"
              >
                {isSubmitting ? "Verifying..." : "Verify & Continue"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

